import { getLocalServerURL, saveTecgymAdminUserAttendanceURL } from "./apiUrl";
import { getAdminUser, getLoggedInAdminLoginId, getLoggedInAdminUserId } from "./auth";
import { _putForm } from "./fetch";
import {
  USER_ATTENDANCE_KEYS,
  fetchTecgymAdminUsers,
  getAdminUserDisplayName,
  readUserListField
} from "./tecgymAdminUsers";

export const HOLIDAY_STORAGE_KEY = "tecGymAdminHolidays";
export const DUTY_START_MINUTES = 9 * 60 + 30;
export const DUTY_END_MINUTES = 18 * 60 + 30;
export const EXPECTED_WORK_MINUTES = DUTY_END_MINUTES - DUTY_START_MINUTES;
export const LATE_AFTER_MINUTES = DUTY_START_MINUTES + 30;
export const CHECK_IN_GRACE_MS = 10 * 60 * 1000;
export const DUTY_HOURS_LABEL = "9:30 AM – 6:30 PM";

const WEEKDAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

function pad2(value) {
  return String(value).padStart(2, "0");
}

export function getLocalDateKey(date = new Date()) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

export function parseDateKey(dateKey) {
  const [year, month, day] = String(dateKey || "").split("-").map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
}

export function getMonthLabel(year, monthIndex) {
  return `${MONTH_NAMES[monthIndex]} ${year}`;
}

export function getDaysInMonth(year, monthIndex) {
  const total = new Date(year, monthIndex + 1, 0).getDate();
  return Array.from({ length: total }, (_, index) => {
    const date = new Date(year, monthIndex, index + 1);
    return {
      date,
      dateKey: getLocalDateKey(date),
      dayName: WEEKDAY_NAMES[date.getDay()],
      dateLabel: `${pad2(date.getDate())} ${MONTH_NAMES[date.getMonth()].slice(0, 3)} ${date.getFullYear()}`
    };
  });
}

export function formatClockTime(value) {
  if (!value) {
    return "—";
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  const hours = date.getHours();
  const minutes = pad2(date.getMinutes());
  const suffix = hours >= 12 ? "PM" : "AM";
  const hour12 = hours % 12 || 12;
  return `${hour12}:${minutes} ${suffix}`;
}

export function formatDuration(totalMinutes) {
  if (totalMinutes == null || Number.isNaN(Number(totalMinutes))) {
    return "—";
  }

  const safeMinutes = Math.max(0, Math.round(Number(totalMinutes)));
  const hours = Math.floor(safeMinutes / 60);
  const minutes = safeMinutes % 60;
  return `${hours}h ${pad2(minutes)}m`;
}

export function getMinutesFromMidnight(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.getHours() * 60 + date.getMinutes();
}

export function isLateCheckIn(checkInTime) {
  const minutes = getMinutesFromMidnight(checkInTime);
  return minutes != null && minutes > LATE_AFTER_MINUTES;
}

export function isEarlyCheckout(checkOutTime) {
  const minutes = getMinutesFromMidnight(checkOutTime);
  return minutes != null && minutes < DUTY_END_MINUTES;
}

export function getCheckInMarkTime(now = new Date()) {
  const minutes = getMinutesFromMidnight(now);
  const inEarlyWindow = minutes != null
    && minutes >= DUTY_START_MINUTES
    && minutes <= LATE_AFTER_MINUTES;

  if (!inEarlyWindow) {
    return new Date(now.getTime());
  }

  return new Date(now.getTime() - CHECK_IN_GRACE_MS);
}

export function calculateWorkingMinutes(checkInTime, checkOutTime) {
  if (!checkInTime || !checkOutTime) {
    return null;
  }

  const start = getMinutesFromMidnight(checkInTime);
  const end = getMinutesFromMidnight(checkOutTime);

  if (start == null || end == null) {
    return null;
  }

  const dutyStart = Math.max(start, DUTY_START_MINUTES);
  const dutyEnd = Math.min(end, DUTY_END_MINUTES);
  return Math.max(0, dutyEnd - dutyStart);
}

export function calculateDeficitMinutes(workingMinutes) {
  if (workingMinutes == null) {
    return null;
  }

  return Math.max(0, EXPECTED_WORK_MINUTES - workingMinutes);
}

export function calculateOvertimeMinutes(checkOutTime) {
  const end = getMinutesFromMidnight(checkOutTime);
  if (end == null) {
    return null;
  }

  return Math.max(0, end - DUTY_END_MINUTES);
}

export function getNetDeficitMinutes(deficitMinutes, overtimeMinutes) {
  const deficit = Math.max(0, Number(deficitMinutes) || 0);
  const overtime = Math.max(0, Number(overtimeMinutes) || 0);
  return Math.max(0, deficit - overtime);
}

export function getEmployeeRecordId(user) {
  return String(
    user?._id ||
    user?.id ||
    user?.userId ||
    user?.userID ||
    user?.adminUserId ||
    user?.adminUserID ||
    user?.tecgymAdminUserId ||
    user?.tecgymAdminUserID ||
    getLoggedInAdminUserId(user) ||
    ""
  ).trim();
}

export function getEmployeeLoginId(user) {
  return String(
    user?.loginId ||
    user?.loginID ||
    user?.loginMail ||
    user?.email ||
    user?.loginEmail ||
    getLoggedInAdminLoginId(user) ||
    ""
  ).trim().toLowerCase();
}

export function getEmployeeName(user) {
  return getAdminUserDisplayName(user) || getLoggedInAdminLoginId(user) || "Employee";
}

export function getCurrentEmployee() {
  const user = getAdminUser() || {};
  const session = user.session && typeof user.session === "object" ? user.session : {};
  return { ...user, ...session };
}

function createRecordId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `att-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function withAttendanceTotals(record) {
  const workingMinutes = calculateWorkingMinutes(record.checkInTime, record.checkOutTime);
  const dateKey = record?.dateKey || "";
  return {
    ...record,
    dayName: record?.dayName || (dateKey ? WEEKDAY_NAMES[parseDateKey(dateKey).getDay()] : "") || "",
    isLate: isLateCheckIn(record.checkInTime),
    isEarlyCheckout: isEarlyCheckout(record.checkOutTime),
    workingMinutes,
    deficitMinutes: calculateDeficitMinutes(workingMinutes),
    overtimeMinutes: calculateOvertimeMinutes(record.checkOutTime)
  };
}

export function normalizeAttendanceRecord(record) {
  if (!record || typeof record !== "object" || Array.isArray(record)) {
    return null;
  }

  const nested = record.attendance || record.data || record.record;
  const source = nested && typeof nested === "object" && !Array.isArray(nested) && !nested.checkInTime && record.checkInTime
    ? record
    : nested && typeof nested === "object" && !Array.isArray(nested)
      ? { ...record, ...nested }
      : record;

  const checkInTime = source.checkInTime || source.checkIn || source.checkInAt || "";
  const dateKey = source.dateKey || (checkInTime ? getLocalDateKey(new Date(checkInTime)) : "");

  return withAttendanceTotals({
    id: String(source._id || source.id || createRecordId()),
    dateKey,
    employeeId: String(source.employeeId || source.userId || source.adminUserId || "").trim(),
    employeeName: String(source.employeeName || source.name || "").trim(),
    employeeEmail: String(source.employeeEmail || source.loginId || source.email || "").trim().toLowerCase(),
    checkInTime: checkInTime || "",
    checkInImage: resolveAttendanceImageUrl(source.checkInImage || source.checkInPhoto || ""),
    checkOutTime: source.checkOutTime || source.checkOut || source.checkOutAt || "",
    checkOutImage: resolveAttendanceImageUrl(source.checkOutImage || source.checkOutPhoto || ""),
    createdAt: source.createdAt || checkInTime || new Date().toISOString(),
    updatedAt: source.updatedAt || new Date().toISOString()
  });
}

function imageValueToString(value) {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    return value.trim();
  }

  if (typeof value === "object") {
    return imageValueToString(
      value.url ||
      value.path ||
      value.src ||
      value.location ||
      value.filename ||
      value.fileName ||
      ""
    );
  }

  return String(value).trim();
}

function resolveAttendanceImageUrl(value) {
  const image = imageValueToString(value);
  if (!image || image.startsWith("data:") || image.startsWith("blob:") || /^https?:\/\//i.test(image)) {
    return image;
  }

  const server = getLocalServerURL().replace(/\/+$/, "");
  return `${server}/${image.replace(/^\/+/, "")}`;
}

function monthDatePrefix(month, year) {
  return `${year}-${String(month).padStart(2, "0")}-`;
}

function dataUrlToBlob(dataUrl) {
  const value = String(dataUrl || "");
  if (!value.startsWith("data:")) {
    return null;
  }

  const [header, base64] = value.split(",");
  if (!base64) {
    return null;
  }

  const mime = header.match(/data:(.*?);/)?.[1] || "image/jpeg";
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new Blob([bytes], { type: mime });
}

function appendAttendanceImage(formData, fieldName, value) {
  const blob = dataUrlToBlob(value);
  if (blob) {
    const extension = blob.type === "image/png" ? "png" : "jpg";
    formData.append(fieldName, blob, `${fieldName}.${extension}`);
    return;
  }

  const image = String(value || "").trim();
  if (image && !image.startsWith("data:") && !image.startsWith("blob:")) {
    formData.append(`${fieldName}Path`, image);
  }
}

function buildAttendanceFormData(record) {
  const formData = new FormData();
  [
    "id",
    "dateKey",
    "dayName",
    "employeeId",
    "employeeName",
    "employeeEmail",
    "checkInTime",
    "checkOutTime",
    "createdAt"
  ].forEach((key) => {
    const value = record?.[key];
    if (value !== undefined && value !== null && value !== "") {
      formData.append(key, String(value));
    }
  });

  appendAttendanceImage(formData, "checkInImage", record?.checkInImage);
  appendAttendanceImage(formData, "checkOutImage", record?.checkOutImage);
  return formData;
}

function collectUserAttendance(user) {
  return readUserListField(user, USER_ATTENDANCE_KEYS)
    .map((item) => normalizeAttendanceRecord({
      ...item,
      employeeId: item?.employeeId || item?.userId || getEmployeeRecordId(user),
      employeeName: item?.employeeName || item?.name || getEmployeeName(user),
      employeeEmail: item?.employeeEmail || item?.email || item?.loginId || getEmployeeLoginId(user)
    }))
    .filter(Boolean);
}

function upsertAttendanceList(records, record) {
  const nextRecord = withAttendanceTotals({
    ...record,
    id: record.id || createRecordId(),
    updatedAt: new Date().toISOString()
  });
  const next = records.filter((item) => item.id !== nextRecord.id);
  const matchIndex = next.findIndex((item) =>
    item.dateKey === nextRecord.dateKey
    && (
      (nextRecord.employeeId && item.employeeId === nextRecord.employeeId)
      || (nextRecord.employeeEmail && item.employeeEmail === nextRecord.employeeEmail)
    )
  );

  if (matchIndex >= 0) {
    const current = next[matchIndex];
    next[matchIndex] = {
      ...current,
      ...nextRecord,
      id: current.id || nextRecord.id,
      checkInImage: nextRecord.checkInImage || current.checkInImage || "",
      checkOutImage: nextRecord.checkOutImage || current.checkOutImage || ""
    };
  } else {
    next.push(nextRecord);
  }

  return {
    records: next,
    saved: next[matchIndex] || nextRecord
  };
}

export function findAdminUser(users, employee) {
  const employeeId = getEmployeeRecordId(employee);
  const employeeEmail = getEmployeeLoginId(employee);
  const employeeName = getEmployeeName(employee).toLowerCase();

  return (Array.isArray(users) ? users : []).find((user) => {
    const userId = getEmployeeRecordId(user);
    if (employeeId && userId && userId === employeeId) {
      return true;
    }

    const userEmail = getEmployeeLoginId(user);
    if (employeeEmail && userEmail && userEmail === employeeEmail) {
      return true;
    }

    return sameEmployeeName(employeeName, getEmployeeName(user));
  }) || null;
}

function sameEmployeeName(left, right) {
  const firstName = String(left || "").trim().toLowerCase().split(/\s+/)[0];
  const secondName = String(right || "").trim().toLowerCase().split(/\s+/)[0];
  return Boolean(firstName) && firstName === secondName;
}

export async function fetchAttendanceRecords(month, year, employee) {
  const users = await fetchTecgymAdminUsers();
  const prefix = monthDatePrefix(month, year);
  const records = users.flatMap(collectUserAttendance).filter((record) =>
    String(record.dateKey || "").startsWith(prefix)
  );

  if (!employee) {
    return records;
  }

  return records.filter((record) => findEmployeeRecord([record], employee, record.dateKey));
}

export async function saveAttendanceRecord(record) {
  const users = await fetchTecgymAdminUsers();
  const user = findAdminUser(users, {
    _id: record?.employeeId,
    email: record?.employeeEmail,
    name: record?.employeeName
  });

  if (!user) {
    throw new Error("Unable to save attendance because this employee was not found.");
  }

  const userId = getEmployeeRecordId(user);
  const employeeName = getEmployeeName(user);
  const { records, saved } = upsertAttendanceList(collectUserAttendance(user), {
    ...record,
    employeeId: userId,
    employeeName: employeeName || record?.employeeName || "",
    employeeEmail: getEmployeeLoginId(user) || record?.employeeEmail || ""
  });

  const response = await _putForm(
    saveTecgymAdminUserAttendanceURL(userId),
    buildAttendanceFormData(saved)
  );
  if (!response || response.status === false || response.success === false) {
    throw new Error(response?.error || response?.message || "Unable to save attendance.");
  }

  const remote = normalizeAttendanceRecord(response.attendance);
  if (!remote?.dateKey) {
    throw new Error("Attendance was not added on this user.");
  }

  return {
    ...saved,
    ...remote,
    id: remote.id || saved.id,
    checkInImage: remote.checkInImage || saved.checkInImage,
    checkOutImage: remote.checkOutImage || saved.checkOutImage
  };
}

export function findEmployeeRecord(records, employee, dateKey) {
  const employeeId = getEmployeeRecordId(employee);
  const employeeEmail = getEmployeeLoginId(employee);
  const employeeName = getEmployeeName(employee).toLowerCase();

  return records.find((record) => {
    if (record.dateKey !== dateKey) {
      return false;
    }

    if (employeeId && record.employeeId && record.employeeId === employeeId) {
      return true;
    }

    if (employeeEmail && record.employeeEmail && record.employeeEmail === employeeEmail) {
      return true;
    }

    return sameEmployeeName(employeeName, record.employeeName);
  }) || null;
}

export function getTodayAttendanceState(records, employee, now = new Date()) {
  const record = findEmployeeRecord(records, employee, getLocalDateKey(now));

  if (!record?.checkInTime) {
    return { mode: "check-in", record: null, buttonLabel: "Mark Attendance" };
  }

  if (!record.checkOutTime) {
    return { mode: "check-out", record, buttonLabel: "Mark Attendance" };
  }

  return { mode: "complete", record, buttonLabel: "Attendance Marked" };
}

export function compressImageDataUrl(dataUrl, maxWidth = 320, quality = 0.55) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement("canvas");
      const scale = Math.min(1, maxWidth / Math.max(image.width, 1));
      canvas.width = Math.max(1, Math.round(image.width * scale));
      canvas.height = Math.max(1, Math.round(image.height * scale));
      const context = canvas.getContext("2d");
      if (!context) {
        resolve(dataUrl);
        return;
      }
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    image.onerror = () => reject(new Error("Unable to process the captured image."));
    image.src = dataUrl;
  });
}

function normalizeHolidayRecord(record) {
  if (!record || typeof record !== "object" || Array.isArray(record)) {
    return null;
  }

  const dateKey = String(record.dateKey || record.date || "").trim();
  const occasion = String(record.occasion || record.holidayOccasion || record.name || record.title || "").trim();

  if (!dateKey || !occasion) {
    return null;
  }

  return {
    id: String(record.id || record._id || `holiday-${dateKey}`),
    dateKey,
    occasion,
    createdAt: record.createdAt || new Date().toISOString(),
    updatedAt: record.updatedAt || new Date().toISOString()
  };
}

function readHolidayRecords() {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(HOLIDAY_STORAGE_KEY);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(normalizeHolidayRecord).filter(Boolean) : [];
  } catch {
    return [];
  }
}

function writeHolidayRecords(records) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(HOLIDAY_STORAGE_KEY, JSON.stringify(records));
  } catch {
    // Ignore storage quota errors.
  }
}

export function fetchHolidayRecords() {
  return readHolidayRecords().sort((a, b) => a.dateKey.localeCompare(b.dateKey));
}

export function getHolidayForDate(holidays, dateKey) {
  if (!dateKey || !Array.isArray(holidays)) {
    return null;
  }

  return holidays.find((holiday) => holiday.dateKey === dateKey) || null;
}

export function saveHolidayRecord({ dateKey, occasion }) {
  const normalizedDateKey = String(dateKey || "").trim();
  const normalizedOccasion = String(occasion || "").trim();

  if (!normalizedDateKey) {
    throw new Error("Please select a holiday date.");
  }

  if (!normalizedOccasion) {
    throw new Error("Please enter the holiday occasion.");
  }

  const records = readHolidayRecords();
  const existingIndex = records.findIndex((item) => item.dateKey === normalizedDateKey);
  const nextRecord = {
    id: existingIndex >= 0 ? records[existingIndex].id : `holiday-${normalizedDateKey}`,
    dateKey: normalizedDateKey,
    occasion: normalizedOccasion,
    createdAt: existingIndex >= 0 ? records[existingIndex].createdAt : new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };

  if (existingIndex >= 0) {
    records[existingIndex] = nextRecord;
  } else {
    records.push(nextRecord);
  }

  writeHolidayRecords(records);
  return nextRecord;
}

export function removeHolidayRecord(dateKey) {
  const next = readHolidayRecords().filter((item) => item.dateKey !== dateKey);
  writeHolidayRecords(next);
  return next;
}
